import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';

export default function Actividades() {
  const company = sessionStorage.getItem('company') || 1;
  const [activities, setActivities] = useState([]);
  const [systemTypes, setSystemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    maintenance_type: 'Todos',
    elevator_type_id: '',
  });

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      const [resAct, resSys] = await Promise.all([
        axios.get(`/getMaintenanceActivities/${company}`),
        axios.get(`/getElevatorTypes/${company}`).catch(() => ({ data: { data: [] } })),
      ]);
      setActivities(resAct.data.data || []);
      setSystemTypes(resSys.data.data || []);
    } catch (error) {
      console.error('Error cargando actividades de mantenimiento:', error);
    } finally {
      setLoading(false);
    }
  }, [company]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleOpenCreate = () => {
    setEditingItem(null);
    setForm({
      name: '',
      description: '',
      maintenance_type: 'Todos',
      elevator_type_id: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '',
      description: item.description || '',
      maintenance_type: item.maintenance_type || 'Todos',
      elevator_type_id: item.elevator_type_id || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      Swal.fire('Atención', 'El nombre de la actividad previa es obligatorio.', 'warning');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        maintenance_type: form.maintenance_type || 'Todos',
        elevator_type_id: form.elevator_type_id || null,
        company,
      };

      if (editingItem) {
        await axios.put(`/updateMaintenanceActivity/${editingItem.id}`, payload);
      } else {
        await axios.post('/saveMaintenanceActivity', payload);
      }

      setDialogOpen(false);
      await fetchActivities();
      Swal.fire({
        icon: 'success',
        title: editingItem ? 'Actividad previa actualizada' : 'Actividad previa creada',
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error guardando actividad:', error);
      Swal.fire('Error', 'No se pudo guardar la actividad.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const confirm = await Swal.fire({
      title: '¿Eliminar actividad previa?',
      text: `Se eliminará la actividad "${item.name}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`/deleteMaintenanceActivity/${item.id}`);
      await fetchActivities();
      Swal.fire({
        icon: 'success',
        title: 'Actividad eliminada',
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire('Error', 'No se pudo eliminar la actividad.', 'error');
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight="700" color="text.primary">
            Catálogo de Actividades / Acciones Previas antes del Mantenimiento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configura las acciones previas (ej. Señalización y demarcación del área, Verificación de EPP, Corte/bloqueo de energía) que el técnico debe verificar antes de iniciar el mantenimiento.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PlusIcon className="h-5 w-5" />}
          onClick={handleOpenCreate}
          sx={{
            borderRadius: 2.5,
            textTransform: 'none',
            fontWeight: '700',
            bgcolor: '#1e3a8a',
            '&:hover': { bgcolor: '#1e40af' },
          }}
        >
          Nueva Actividad Previa
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: '800' }}>#</TableCell>
                <TableCell sx={{ fontWeight: '800' }}>Acción / Actividad Previa</TableCell>
                <TableCell sx={{ fontWeight: '800' }}>Descripción</TableCell>
                <TableCell sx={{ fontWeight: '800' }}>Tipo Mantenimiento</TableCell>
                <TableCell sx={{ fontWeight: '800' }}>Sistema Motriz</TableCell>
                <TableCell align="right" sx={{ fontWeight: '800' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <WrenchScrewdriverIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <Typography variant="body2" color="text.secondary">
                      No hay actividades previas registradas aún. Haz clic en &quot;Nueva Actividad Previa&quot; para agregar la primera.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                activities.map((act, idx) => (
                  <TableRow key={act.id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: '700', color: '#0f172a' }}>{act.name}</TableCell>
                    <TableCell sx={{ color: '#475569' }}>{act.description || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={act.maintenance_type || 'Todos'}
                        size="small"
                        color={
                          act.maintenance_type === 'Preventivo'
                            ? 'success'
                            : act.maintenance_type === 'Correctivo'
                            ? 'warning'
                            : act.maintenance_type === 'Emergencia'
                            ? 'error'
                            : 'default'
                        }
                        variant="outlined"
                        sx={{ fontWeight: '700', fontSize: '0.72rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      {act.systemType?.elevatorType || 'Todos los sistemas'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(act)}>
                        <PencilIcon className="h-4 w-4" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(act)}>
                        <TrashIcon className="h-4 w-4" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: '800' }}>
          {editingItem ? 'Editar Actividad Previa al Mantenimiento' : 'Nueva Actividad Previa al Mantenimiento'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nombre de la Acción / Actividad Previa *"
              placeholder="Ej: Señalización del área y verificación de EPP"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Descripción / Instrucción Previa (Opcional)"
              placeholder="Detalle de la verificación o acción previa antes de iniciar el mantenimiento..."
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Tipo de Mantenimiento Asociado</InputLabel>
              <Select
                label="Tipo de Mantenimiento Asociado"
                value={form.maintenance_type}
                onChange={(e) => setForm((prev) => ({ ...prev, maintenance_type: e.target.value }))}
              >
                <MenuItem value="Todos">Todos (Preventivo, Correctivo y Emergencia)</MenuItem>
                <MenuItem value="Preventivo">Preventivo</MenuItem>
                <MenuItem value="Correctivo">Correctivo</MenuItem>
                <MenuItem value="Emergencia">Emergencia</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Sistema Motriz (Opcional)</InputLabel>
              <Select
                label="Sistema Motriz (Opcional)"
                value={form.elevator_type_id}
                onChange={(e) => setForm((prev) => ({ ...prev, elevator_type_id: e.target.value }))}
              >
                <MenuItem value="">Todos los sistemas motrices</MenuItem>
                {systemTypes.map((st) => (
                  <MenuItem key={st.id} value={st.id}>
                    {st.elevatorType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            sx={{ textTransform: 'none', fontWeight: '700', borderRadius: 2 }}
          >
            {saving ? 'Guardando...' : 'Guardar Actividad'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
